import { captureException, captureMessage } from '@sentry/react';
import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { AppointmentSidebar } from '../AppointmentSidebar';
import { Header } from '../Header';
import { ProgressBar } from '../ProgressBar';
import { useShouldHideCloseButton } from 'src/hooks/shouldHideCloseButton';
import { usePageTitle } from 'src/hooks/usePageTitle';
import { useSelector } from 'src/store';

export function BookingFlowLayout({ children }: React.PropsWithChildren) {
    const { clinicId, doctorErpId, appointmentTypeId } = useSelector((state) => state.appointment);
    const hideCloseButton = useShouldHideCloseButton();
    usePageTitle();
    const [search] = useSearchParams();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (search.has('fallback')) {
            captureException('Opened fallback', {
                level: 'info',
                extra: {
                    fallback: search.get('fallback'),
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!clinicId || !doctorErpId || !appointmentTypeId) {
            captureMessage(
                `No data for ${Object.entries({
                    'Clinic ID': !!clinicId,
                    'Doctor ID': !!doctorErpId,
                    'Appointment Type': !!appointmentTypeId,
                })
                    .filter(([_, v]) => !v)
                    .map(([k, _]) => k)
                    .join(', ')}.`
            );

            // TODO future-proof routes
            if (
                [
                    // routes where appt is certainly completed/confirmed
                    'app/consent',
                    'app/additional-details',
                ].indexOf(pathname.replaceAll(/^\/|\/$/g, '')) > -1
            ) {
                navigate('/error/no-session-data/confirmed');
            } else {
                navigate('/error/no-session-data');
            }
        }
    }, [clinicId, doctorErpId, appointmentTypeId, navigate, pathname]);

    return (
        <div className="flex min-h-dvh flex-col">
            <Header progressBar hideCloseButton={hideCloseButton} className="hidden lg:block" />
            <div className="flex flex-1 flex-col lg:flex-row">
                <aside className="w-full border-r border-gray-200 bg-gray-50 p-4 lg:max-w-md lg:p-8">
                    <AppointmentSidebar />
                </aside>
                <ProgressBar className="block lg:hidden" />
                <main className="relative flex flex-1 bg-white lg:py-8">
                    <div className="mx-auto flex max-w-lg flex-1 flex-col pt-5 2xl:mx-72">
                        <Outlet />
                        {children}
                    </div>
                </main>
            </div>
        </div>
    );
}
